import { useEffect, useState } from "react";
import type { TimedLeaderboard } from "@mk/shared";
import { ScreenHeader } from "../components/ScreenHeader.tsx";
import { api } from "../lib/api.ts";
import { useSession } from "../lib/session.ts";
import { messages } from "../i18n/index.ts";

type Period = "weekly" | "monthly" | "all";
type Board = "duel" | "timed";
interface Entry { userId: string; username: string; wins: number; rank: number }
interface Ranking { entries: Entry[]; me: Entry | null }

/** Ekranın ortak satır biçimi: iki tablo da sıra, ad ve skor metni gösterir. */
interface Row { userId: string; username: string; rank: number; score: string }

const BOARD_KEY = "mk-leaderboard-board";

function storedBoard(): Board {
  try {
    return window.localStorage.getItem(BOARD_KEY) === "timed" ? "timed" : "duel";
  } catch {
    return "duel";
  }
}

async function loadRows(board: Board, period: Period): Promise<{ entries: Row[]; me: Row | null }> {
  if (board === "timed") {
    const data = await api<TimedLeaderboard>(`/api/solo/timed/leaderboard?period=${period}`);
    const row = (entry: TimedLeaderboard["entries"][number]): Row => ({
      userId: entry.userId, username: entry.username, rank: entry.rank,
      score: messages.leaderboard.timedScore(entry.wordsSolved, entry.solvedAttempts),
    });
    return { entries: data.entries.map(row), me: data.me ? row(data.me) : null };
  }
  const data = await api<Ranking>(`/api/duels/leaderboard?period=${period}`);
  const row = (entry: Entry): Row => ({
    userId: entry.userId, username: entry.username, rank: entry.rank,
    score: `${entry.wins} ${messages.leaderboard.wins}`,
  });
  return { entries: data.entries.map(row), me: data.me ? row(data.me) : null };
}

export function LeaderboardScreen({ onBack }: { onBack: () => void }) {
  const [board, setBoard] = useState<Board>(storedBoard);
  const [period, setPeriod] = useState<Period>("weekly");
  const [ranking, setRanking] = useState<{ entries: Row[]; me: Row | null } | null>(null);
  const [failed, setFailed] = useState(false);
  const userId = useSession((state) => state.user?.id);
  useEffect(() => {
    let active = true;
    setRanking(null);
    setFailed(false);
    void loadRows(board, period).then((data) => {
      if (active) setRanking(data);
    }).catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, [board, period]);

  function selectBoard(next: Board) {
    setBoard(next);
    try {
      window.localStorage.setItem(BOARD_KEY, next);
    } catch {
      // Bellekte devam.
    }
  }

  return <div className="app">
    <ScreenHeader title={messages.leaderboard.title} onBack={onBack} />
    <main className="overflow-y-auto px-5 py-5">
      <div className="mx-auto flex w-full max-w-md flex-col gap-4">
        <div className="history-tabs leaderboard-boards" role="radiogroup" aria-label={messages.leaderboard.boardLabel}>
          {(["duel", "timed"] as const).map((item) =>
            <button key={item} type="button" role="radio" aria-checked={board === item} className="history-tab"
              onClick={() => selectBoard(item)}>{messages.leaderboard.boards[item]}</button>)}
        </div>
        <div className="team-browser-tabs" role="tablist" aria-label={messages.leaderboard.title}>
          {(["weekly", "monthly", "all"] as const).map((item) =>
            <button key={item} type="button" role="tab" aria-selected={period === item}
              onClick={() => setPeriod(item)}>{messages.leaderboard.period[item]}</button>)}
        </div>
        {board === "timed" && <p className="text-xs leading-relaxed text-muted">{messages.leaderboard.timedHint}</p>}
        {failed && <p role="alert" className="text-flame">{messages.leaderboard.failed}</p>}
        {!ranking && !failed && <p className="text-muted">{messages.common.loading}</p>}
        {ranking && <>
          {ranking.entries.length === 0 && <p className="text-muted">
            {board === "timed" ? messages.leaderboard.timedEmpty : messages.leaderboard.empty}
          </p>}
          <ol className="flex flex-col gap-2" aria-label={messages.leaderboard.title}>
            {ranking.entries.map((entry) => <li key={entry.userId} className="mode-row" aria-current={entry.userId === userId ? "true" : undefined}>
              <span><b className="mr-3 text-mint">#{entry.rank}</b>{entry.username}</span>
              <b>{entry.score}</b>
            </li>)}
          </ol>
          {ranking.me && !ranking.entries.some((entry) => entry.userId === ranking.me?.userId) &&
            <div className="mode-row"><span>#{ranking.me.rank} · {ranking.me.username}</span><b>{ranking.me.score}</b></div>}
        </>}
      </div>
    </main>
  </div>;
}
